import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authClient } from '../lib/authClient';
import { Mark } from '../components/brand/Mark';

/**
 * F0 Sign In, per `nelnela Sign In.dc.html`. Google SSO primary, email magic link the fallback
 * (ADR-0005). The logic is M2's, untouched — this milestone is the surface.
 *
 * Three things the canvas has that this does not, each a decision rather than an omission:
 *   · **no Apple button** — the canvas prop `showApple` is off here; ADR-0005 is Google plus
 *     magic link, and Apple is a rung-3 question with a developer-account bill attached.
 *   · **no terms/privacy line** — those documents do not exist yet, and a link to a page that
 *     is not there is worse than no link. It returns with the Play listing (M8).
 *   · **the OR label is not monospace** — monospace on these canvases is annotation, never
 *     product UI (design README §5).
 *
 * The canvas is dark-only; every colour below goes through a token or `light-dark()`, so the
 * light scheme reads correctly without a second design.
 */
export function SignIn() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Absolute callback so the round-trip returns to the origin you started on (in dev the
  // Vite server on :5173 proxies /api to the Worker on :8787; the link itself is minted with
  // the Worker's baseURL, and the session cookie is shared across localhost ports).
  const callbackURL = `${window.location.origin}/`;
  const google = () => authClient.signIn.social({ provider: 'google', callbackURL });
  const magic = async () => {
    setErr(null);
    setBusy(true);
    try {
      const r = await authClient.signIn.magicLink({ email, callbackURL });
      if (r.error) setErr(r.error.message ?? 'Could not send the link.');
      else setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'light-dark(var(--nn-bg), var(--mantine-color-dark-9))',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* The ember glow — the only decoration on the screen, and it carries no meaning. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '16%',
          transform: 'translateX(-50%)',
          width: 520,
          height: 340,
          maxWidth: '100%',
          borderRadius: '50%',
          background:
            'radial-gradient(ellipse, color-mix(in srgb, var(--nn-accent) 8%, transparent), transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ padding: '26px 34px' }}>
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            textDecoration: 'none',
          }}
        >
          <Mark size={24} />
          <span
            style={{
              font: "500 15px/1 'Noto Sans Georgian', sans-serif",
              letterSpacing: '-.01em',
              color: 'color-mix(in srgb, var(--nn-text) 80%, var(--nn-muted))',
            }}
          >
            nelnela
          </span>
        </Link>
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 24px 60px',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: 384,
            maxWidth: '100%',
            background: 'light-dark(var(--nn-panel), var(--nn-bg))',
            border: '1px solid var(--nn-hairline)',
            borderRadius: 16,
            padding: '34px 32px 28px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
              textAlign: 'center',
            }}
          >
            <Mark size={46} title="nelnela" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
                style={{
                  font: "600 22px/1.25 'Noto Serif Georgian', serif",
                  color: 'var(--nn-text-strong)',
                }}
              >
                Welcome to nelnela
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--nn-muted)' }}>
                Sign in or create your account — same door.
              </div>
            </div>
          </div>

          {sent ? (
            <SentState email={email} onReset={() => setSent(false)} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
              <button type="button" className="nn-sso-btn" onClick={google}>
                <GoogleGlyph />
                Continue with Google
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ flex: 1, height: 1, background: 'var(--nn-hairline)' }} />
                {/* Not monospace: that is canvas annotation, not product type (README §5). */}
                <span style={{ fontSize: 10, letterSpacing: '.08em', color: 'var(--nn-faint)' }}>
                  OR
                </span>
                <span style={{ flex: 1, height: 1, background: 'var(--nn-hairline)' }} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="email"
                  className="nn-email-input"
                  placeholder="you@anywhere.com"
                  value={email}
                  autoComplete="email"
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && email && !busy) void magic();
                  }}
                  aria-label="Email address"
                />
                <button
                  type="button"
                  className="nn-ember-btn"
                  onClick={() => void magic()}
                  disabled={!email || busy}
                >
                  Continue with email
                </button>
                <div
                  style={{
                    fontSize: 12,
                    lineHeight: 1.55,
                    color: 'var(--nn-faint)',
                    textAlign: 'center',
                  }}
                >
                  New here? Same button — we’ll send a sign-in link.
                </div>
                {err && (
                  <div
                    style={{
                      fontSize: 12,
                      lineHeight: 1.55,
                      color: 'var(--mantine-color-red-4)',
                      textAlign: 'center',
                    }}
                  >
                    {err}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 22,
            font: "12px/1 'Noto Serif Georgian', serif",
            color: 'var(--nn-faint)',
          }}
        >
          <span lang="ka">ნელ-ნელა</span> · little by little
        </div>
      </div>
    </div>
  );
}

/** The magic-link sent state (Screens `8b`) — the copy is M2's, which the design pass kept. */
function SentState({ email, onReset }: { email: string; onReset: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'center' }}>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--nn-text-strong)' }}>
        Check your email
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--nn-muted)' }}>
        We sent a link to {email}. It expires in 10 minutes.
      </div>
      <button
        type="button"
        onClick={onReset}
        style={{
          marginTop: 4,
          background: 'none',
          border: 'none',
          color: 'var(--nn-link)',
          fontSize: 12.5,
          cursor: 'pointer',
        }}
      >
        Use a different address
      </button>
    </div>
  );
}

/** Google's own four-colour G, from the canvas — their mark, drawn their way. */
const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path
      d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"
      fill="#FFC107"
    />
    <path
      d="M4.5 14.7l7 5.1C13.4 15.1 18.3 11 24 11c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 15.5 2 8.1 6.7 4.5 14.7z"
      fill="#FF3D00"
    />
    <path
      d="M24 46c5.5 0 10.4-1.8 14.1-4.9l-6.5-5.5C29.5 37.1 26.9 38 24 38c-6 0-10.6-3-12.4-8.2l-7 5.4C8.2 41.5 15.5 46 24 46z"
      fill="#4CAF50"
    />
    <path
      d="M44.5 20H24v8.5h11.8c-.9 2.5-2.5 4.6-4.7 6.1l6.5 5.5C41.5 36.6 45 31 45 24c0-1.3-.2-2.7-.5-4z"
      fill="#1976D2"
    />
  </svg>
);

/**
 * Hover and focus for the three custom controls above. Inline styles cannot express a
 * pseudo-class, and these are hand-built DOM rather than Mantine components, so they get a
 * stylesheet — the same move `READING_SURFACE_CSS` makes for the transcript.
 */
export const SIGN_IN_CSS = `
.nn-sso-btn {
  display: flex; align-items: center; justify-content: center; gap: 11px;
  height: 48px; width: 100%;
  background: color-mix(in srgb, var(--nn-bg) 55%, var(--nn-panel));
  border: 1px solid color-mix(in srgb, var(--nn-text) 13%, transparent);
  border-radius: 9px; color: var(--nn-text-strong);
  font: 500 14.5px/1 'Noto Sans Georgian', sans-serif; cursor: pointer;
}
.nn-sso-btn:hover {
  background: color-mix(in srgb, var(--nn-bg) 35%, var(--nn-panel));
  border-color: color-mix(in srgb, var(--nn-text) 22%, transparent);
}
.nn-email-input {
  height: 48px; width: 100%; box-sizing: border-box;
  background: light-dark(var(--nn-panel), color-mix(in srgb, var(--nn-bg) 60%, var(--mantine-color-dark-9)));
  border: 1px solid color-mix(in srgb, var(--nn-text) 14%, transparent);
  border-radius: 9px; padding: 0 15px; color: var(--nn-text-strong);
  font: 14.5px/1 'Noto Sans Georgian', sans-serif; outline: none;
}
.nn-email-input::placeholder { color: var(--nn-faint); }
.nn-email-input:focus { border-color: color-mix(in srgb, var(--nn-accent) 65%, transparent); }
.nn-ember-btn {
  height: 48px; width: 100%;
  background: var(--nn-accent); border: none; border-radius: 9px;
  color: var(--nn-on-accent);
  font: 600 15px/1 'Noto Sans Georgian', sans-serif; cursor: pointer;
}
.nn-ember-btn:hover:not(:disabled) { background: var(--mantine-color-ember-4); }
.nn-ember-btn:disabled { opacity: .55; cursor: default; }
`;
